import { CommandNames, CommandParams, SxcGlobal } from '@2sic.com/2sxc-typings';
import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CatalogState } from 'src/app/root-store/catalog-store/state';
import { selectCatalog } from '../../root-store/catalog-store/selectors';
import { RootState } from '../../root-store/root-state.model';
import { AdminBackendService } from '../admin-backend.service';
import { ItemEditModel } from '../items/items.models';
import { ScriptService } from '../script.service';
import { SxcConfig } from './sxc.model';

declare var $2sxc: SxcGlobal

@Injectable({
  providedIn: 'root'
})
export class SxcService {
  private catalog!: CatalogState
  itemsConfig: SxcConfig | undefined
  private scriptsLoaded = new BehaviorSubject<boolean>(false)
  public static readonly defaultAppId = 3

  constructor(
    private script: ScriptService,
    private store: Store<RootState>,
    private adminBackend: AdminBackendService,
    private _ngZone: NgZone
  ) { }

  init(sxcConfig: SxcConfig | null = null): Observable<any> {
    let configLoaded$: Observable<boolean>
    if (sxcConfig) {
      this.itemsConfig = sxcConfig
      configLoaded$ = of(true)
    }
    else
      configLoaded$ = this.adminBackend.getSxcConfig().pipe(tap(c => this.itemsConfig = c), map(() => true))

    this.catalog = this.store.selectSignal(selectCatalog)()

    return combineLatest([this.loadScripts(), configLoaded$]).pipe(filter(([s, c]) => s && c))
  }

  private loadScripts(): Observable<boolean> {
    if (!this.scriptsLoaded.value)
      this.script.load('sxc-api').then(() => {
        $2sxc.env.load({
          page: this.catalog.id,
          rvt: document.getElementById('__RequestVerificationToken')?.getAttribute('value'), // request verification token
          // root: '/', // this is your website root
          api: '/api/sxc/', // the api base path
          uiRoot: '/Sxc/core/', // the path above the js/dist which contains everything
        } as any)

        this.script.load('sxc-inpage')
          .then(() => this.scriptsLoaded.next(true))
      })

    return this.scriptsLoaded
  }

  public editItem(itemData: ItemEditModel, contentTypeNames: string[], onClose: () => void): void {
    const prefill = { ItemId: itemData.id, CatalogId: this.catalog.id, ParentCatalogId: this.catalog.parentCatalogId }
    const forRef = { Number: itemData.id, Target: this.itemsConfig!.targetTypeRoom, TargetType: this.itemsConfig!.targetTypeRoom, Singleton: true }
    const items: { [key: string]: any }[] = []

    // append all specified ContentTypes
    contentTypeNames.forEach(ContentTypeName => {
      const item = { ContentTypeName, For: forRef, Prefill: prefill } as any

      if (ContentTypeName === 'Description') // for Description: also add Name to prefill
        item.Prefill = { ItemId: prefill.ItemId, CatalogId: this.catalog.id, Name: itemData.name, ParentCatalogId: prefill.ParentCatalogId }

      items.push(item)
    })

    // when editing Description, append customer-specific ContentTypes
    if (contentTypeNames.includes('Description'))
      this.itemsConfig!.catalogContentTypes
        .filter((t) => t.itemType === itemData.itemType)
        .forEach(t => items.push({ ContentTypeName: t.name, For: forRef, Prefill: prefill }))

    this.editEntityInternal({ items }, onClose)
  }

  public editEntity(entityId: number, onClose: () => void): void {
    this.init().pipe(take(1)).subscribe(() => {
      this.editEntityInternal({ entityId }, onClose)
    })
  }

  public addEntity(contentTypeName: string, onClose: () => void) {
    this.init().pipe(take(1)).subscribe(() => {
      const prefill = { CatalogId: this.catalog.id }

      const item = { ContentTypeName: contentTypeName, Prefill: prefill } as any

      this.editEntityInternal({ items: [item] }, onClose)
    })
  }

  private editEntityInternal(params: CommandParams, onClose = () => { }): void {
    // This is the workflow step we will register to stop page refresh
    const workflowToDisableRefresh = {
      command: 'refresh',   // The command name it's for
      phase: 'before',      // The workflow-step should run before the command is executed
      code: (wfArgs: any) => {   // The code which should be run
        if (onClose)
          this._ngZone.run(() => onClose())

        return false;       // Return false to stop this command from happening
      }
    }

    this.getSxc().cms.run({ params, action: CommandNames.edit, workflows: [workflowToDisableRefresh] })
  }

  private getSxc() {
    return $2sxc({ zoneId: this.itemsConfig!.zoneId, appId: this.itemsConfig!.appId, pageId: this.catalog.id, moduleId: this.catalog.id })
  }

  openAppAdmin(): void {
    this.init()
      .pipe(take(1))
      .subscribe(() => this.getSxc().cms.run({
        action: CommandNames.app,
        workflows: [{ command: 'refresh', phase: 'before', code: () => false }]
      }))
  }

  clearCache(): Observable<string> {
    return this.init().pipe(
      take(1),
      switchMap(() => this.adminBackend.clearSxcCache(this.itemsConfig!.appId)))
  }
}
